import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Users, UserCheck, TrendingUp, RefreshCw, Mic, X, Target, UserPlus, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/config/api";
import { StatCard } from "@/components/StatCard";
import { MasteryStars } from "@/components/VerseCard";
import { GamifiedButton } from "@/components/GamifiedButton";
import { Loading, EmptyState } from "@/components/Feedback";
import { useSeo } from "@/hooks/useSeo";

const Avatar = ({ s }) => {
  const initials = `${s.first_name?.[0] || ""}${s.last_name?.[0] || ""}`.toUpperCase();
  return s.avatar_url
    ? <img src={`${process.env.REACT_APP_BACKEND_URL}${s.avatar_url}`} alt="" className="w-11 h-11 rounded-2xl object-cover" />
    : <div className="w-11 h-11 rounded-2xl bg-secondary/15 text-secondary font-extrabold flex items-center justify-center">{initials || "?"}</div>;
};

export default function Teacher() {
  useSeo("Espace Prof", "Espace professeur Quran Coach : rattache tes élèves, suis leur progression, leurs révisions et récitations, et fixe des objectifs.");
  const qc = useQueryClient();
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [email, setEmail] = useState("");
  const [goal, setGoal] = useState("");
  const { data: dash, isLoading } = useQuery({ queryKey: ["teacher-dash"], queryFn: async () => (await api.get("/teacher/dashboard")).data });
  const { data: students } = useQuery({ queryKey: ["teacher-students"], queryFn: async () => (await api.get("/teacher/students")).data });
  const { data: detail } = useQuery({
    queryKey: ["teacher-student", selected],
    queryFn: async () => (await api.get(`/teacher/students/${selected}`)).data,
    enabled: !!selected,
  });

  const refreshLists = () => {
    qc.invalidateQueries({ queryKey: ["teacher-students"] });
    qc.invalidateQueries({ queryKey: ["teacher-dash"] });
  };

  const attach = useMutation({
    mutationFn: async () => (await api.post("/teacher/students", { email, goal })).data,
    onSuccess: (s) => {
      toast.success(`${s.first_name} a été rattaché à votre profil.`);
      setShowAdd(false); setEmail(""); setGoal("");
      refreshLists();
    },
    onError: (err) => toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"),
  });

  const detach = useMutation({
    mutationFn: async (sid) => (await api.delete(`/teacher/students/${sid}`)).data,
    onSuccess: () => { toast.success("Élève retiré."); refreshLists(); },
    onError: () => toast.error("Erreur lors du retrait."),
  });

  if (isLoading) return <Loading full />;

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-black text-slate-800">Espace Prof</h1>
          <p className="text-slate-500">Suis la progression de tes élèves.</p>
        </div>
        <GamifiedButton onClick={() => setShowAdd(true)} className="py-3" data-testid="add-student-btn">
          <UserPlus className="w-5 h-5" /> Ajouter un élève
        </GamifiedButton>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard icon={Users} label="Élèves" value={dash.students_count} tint="primary" />
        <StatCard icon={UserCheck} label="Actifs" value={dash.active_students} tint="success" />
        <StatCard icon={TrendingUp} label="Moy. min" value={dash.avg_minutes} tint="secondary" />
        <StatCard icon={RefreshCw} label="Révisions" value={dash.reviews_today} tint="accent" />
        <StatCard icon={Mic} label="Récitations" value={dash.recitations_to_check} tint="primary" />
      </div>

      <section>
        <h2 className="font-black text-slate-800 mb-4">Mes élèves</h2>
        {!students?.length ? (
          <EmptyState icon={Users} title="Aucun élève rattaché" subtitle="Les élèves rattachés à votre compte apparaîtront ici." />
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {students.map((s, i) => (
              <motion.div key={s.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                className="flex items-center gap-4 bg-white p-4 rounded-3xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:-translate-y-1 transition-transform">
                <button onClick={() => setSelected(s.id)} data-testid={`student-card-${s.id}`} className="flex items-center gap-4 flex-1 min-w-0 text-left">
                  <Avatar s={s} />
                  <div className="flex-1 min-w-0">
                    <div className="font-extrabold text-slate-800">{s.first_name} {s.last_name}</div>
                    <div className="text-xs text-slate-400">{s.verses_learned} versets · {s.verses_mastered} maîtrisés · série {s.streak}j</div>
                  </div>
                </button>
                <span className="text-xs font-bold bg-slate-100 text-slate-500 px-2.5 py-1 rounded-full">{s.age_category}</span>
                <button onClick={() => detach.mutate(s.id)} data-testid={`remove-student-${s.id}`}
                  className="w-9 h-9 rounded-xl text-slate-300 hover:text-red-500 hover:bg-red-50 flex items-center justify-center transition-colors" aria-label="Retirer">
                  <Trash2 className="w-4 h-4" />
                </button>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      <AnimatePresence>
        {selected && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-slate-900/40" onClick={() => setSelected(null)}>
            <motion.div initial={{ y: 40 }} animate={{ y: 0 }} exit={{ y: 40 }} onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg bg-white rounded-3xl p-6 max-h-[85vh] overflow-y-auto" data-testid="student-detail-modal">
              {!detail ? <Loading /> : (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <Avatar s={detail.student} />
                      <div>
                        <div className="font-black text-slate-800 text-lg">{detail.student.first_name} {detail.student.last_name}</div>
                        <div className="text-xs text-slate-400">{detail.student.email}</div>
                      </div>
                    </div>
                    <button onClick={() => setSelected(null)} data-testid="student-detail-close"><X className="w-5 h-5 text-slate-400" /></button>
                  </div>

                  <div className="flex items-center gap-2 bg-primary/5 rounded-2xl p-3 mb-4 text-sm font-bold text-slate-600">
                    <Target className="w-4 h-4 text-primary" /> Objectif : {detail.student.goal || "Non défini"}
                  </div>

                  <div className="grid grid-cols-3 gap-3 mb-5">
                    <div className="text-center bg-slate-50 rounded-2xl py-3"><div className="text-2xl font-black text-slate-800">{detail.student.verses_learned}</div><div className="text-[10px] font-bold uppercase text-slate-400">Appris</div></div>
                    <div className="text-center bg-slate-50 rounded-2xl py-3"><div className="text-2xl font-black text-slate-800">{detail.student.verses_mastered}</div><div className="text-[10px] font-bold uppercase text-slate-400">Maîtrisés</div></div>
                    <div className="text-center bg-slate-50 rounded-2xl py-3"><div className="text-2xl font-black text-slate-800">{detail.student.total_minutes}</div><div className="text-[10px] font-bold uppercase text-slate-400">Minutes</div></div>
                  </div>

                  <h3 className="font-black text-slate-800 mb-2 text-sm">Versets en cours</h3>
                  <div className="space-y-2 mb-5">
                    {detail.progress.length === 0 && <p className="text-sm text-slate-400">Aucun verset encore.</p>}
                    {detail.progress.slice(0, 10).map((p) => (
                      <div key={`${p.surah_number}-${p.ayah_number}`} className="flex items-center justify-between bg-slate-50 rounded-xl px-3 py-2">
                        <span className="text-sm font-bold text-slate-600">Sourate {p.surah_number} · v.{p.ayah_number}</span>
                        <MasteryStars level={p.level} size={3} />
                      </div>
                    ))}
                  </div>

                  <h3 className="font-black text-slate-800 mb-2 text-sm">Récitations</h3>
                  <div className="space-y-2">
                    {detail.recitations.length === 0 && <p className="text-sm text-slate-400">Aucune récitation.</p>}
                    {detail.recitations.slice(0, 5).map((r) => (
                      <div key={r.id} className="flex items-center justify-between bg-slate-50 rounded-xl px-3 py-2">
                        <span className="text-sm font-bold text-slate-600">Sourate {r.surah_number} · v.{r.ayah_number}</span>
                        <span className={`font-black ${r.score >= 85 ? "text-emerald-600" : "text-yellow-600"}`}>{r.score}%</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add student dialog */}
      <AnimatePresence>
        {showAdd && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-slate-900/40" onClick={() => setShowAdd(false)}>
            <motion.div initial={{ y: 40 }} animate={{ y: 0 }} exit={{ y: 40 }} onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-white rounded-3xl p-6" data-testid="add-student-modal">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-black text-slate-800 text-lg">Rattacher un élève</h3>
                <button onClick={() => setShowAdd(false)} data-testid="add-student-close"><X className="w-5 h-5 text-slate-400" /></button>
              </div>
              <p className="text-sm text-slate-500 mb-4">Saisis l'email d'un élève déjà inscrit pour l'ajouter à ton profil.</p>
              <form onSubmit={(e) => { e.preventDefault(); attach.mutate(); }} className="space-y-3">
                <input data-testid="add-student-email" type="email" required placeholder="Email de l'élève" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-2xl border-2 border-slate-200 focus:border-primary outline-none font-semibold" />
                <input data-testid="add-student-goal" placeholder="Objectif (facultatif)" value={goal}
                  onChange={(e) => setGoal(e.target.value)}
                  className="w-full px-4 py-3 rounded-2xl border-2 border-slate-200 focus:border-primary outline-none font-semibold" />
                <GamifiedButton type="submit" disabled={attach.isPending} className="w-full" data-testid="add-student-submit">
                  {attach.isPending ? "Ajout…" : "Rattacher l'élève"}
                </GamifiedButton>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
